import { ValidatorFn, Validators } from '@angular/forms';
import { roundToPrecision } from '@shared/utils';
import { ROW_COST_INPUT_CONFIG } from './delivery-pallets.constants';
import { OptionsSeatInput, PalletSeat } from './delivery-pallets.models';

// Splits rowCost into `amount` per-place costs whose sum equals rowCost exactly (to the cent).
// A plain rowCost/amount split loses cents to rounding (e.g. 100/3 -> 33.33 each, summing to
// 99.99), so the leftover cents from the division are added one-by-one to the first places
// instead of being dropped.
export function distributeRowCost(rowCost: string, amount: number): string[] {
  if (!amount) {
    return [];
  }
  const totalCents = Math.round((Number(rowCost) || 0) * 100);
  if (!totalCents) {
    return fillArray([], '', amount);
  }
  const baseCents = Math.floor(totalCents / amount);
  const remainderCents = totalCents - baseCents * amount;
  return Array.from({ length: amount }, (_, index) =>
    roundToPrecision(((baseCents + (index < remainderCents ? 1 : 0)) / 100), 2),
  );
}

export function rowCostValidators(isTemplate: boolean = false): ValidatorFn {
  return Validators.compose([
    ...(isTemplate ? [] : [Validators.required]),
    Validators.min(ROW_COST_INPUT_CONFIG.minValue),
    Validators.max(ROW_COST_INPUT_CONFIG.maxValue),
  ]);
}

export function fillArray<T>(arr: T[], val: T, times: number): T[] {
  if (times > 0) {
    arr.length = times;
    arr.fill(val);
  } else {
    arr.length = 0;
  }
  return arr;
}

export function comparePallets(option: Record<string, string>, value: Record<string, string>): boolean {
  return option && value && option.volumetricWeight === value.volumetricWeight;
}

export function mapOptionsSeatInputToSeats(opts: OptionsSeatInput, options: Record<string, string>[]): PalletSeat[] {
  const { optionsVar, amounts, costs } = opts;
  return optionsVar
    .map(
      (i, index): PalletSeat => ({
        amount: amounts[index],
        option: options.find((opt) => opt.volumetricWeight === i),
        cost: costs?.[index] ? roundToPrecision(Number(costs[index]), 2).toString() : '',
      }),
    )
    .filter((seat) => seat.option && seat.amount);
}

// Groups a flat API OptionsSeat list (one entry per physical pallet) back into rows.
// distributeRowCost() (used when building the payload) always produces a non-increasing
// per-pallet cost sequence within a row, with at most 1 cent between its highest and lowest
// value, so a run of same-weight pallets is kept as one row as long as it keeps that shape.
// A new row starts once the weight changes or the sequence breaks that pattern - two rows of
// the same pallet type but different RowCost only merge back together in the rare case where
// their costs happen to fit the same non-increasing, <=1-cent-wide pattern.
export function mapApiOptionsSeatToInput(optionsSeat: Record<string, unknown>[]): OptionsSeatInput {
  const rows: {
    volumetricWeight: string;
    amount: number;
    totalCents: number;
    lastCostCents: number;
    minCostCents: number;
    maxCostCents: number;
  }[] = [];
  (Object.values(optionsSeat || {}) as any[]).forEach((ite: any) => {
    const volumetricWeight = Number(ite.volumetricWeight).toString();
    const costCents = Math.round((Number(ite.cost) || 0) * 100);
    const lastRow = rows[rows.length - 1];
    const continuesRow =
      lastRow &&
      lastRow.volumetricWeight === volumetricWeight &&
      costCents <= lastRow.lastCostCents &&
      Math.max(lastRow.maxCostCents, costCents) - Math.min(lastRow.minCostCents, costCents) <= 1;
    if (continuesRow) {
      lastRow.amount += 1;
      lastRow.totalCents += costCents;
      lastRow.lastCostCents = costCents;
      lastRow.minCostCents = Math.min(lastRow.minCostCents, costCents);
      lastRow.maxCostCents = Math.max(lastRow.maxCostCents, costCents);
    } else {
      rows.push({
        volumetricWeight,
        amount: 1,
        totalCents: costCents,
        lastCostCents: costCents,
        minCostCents: costCents,
        maxCostCents: costCents,
      });
    }
  });

  return {
    optionsVar: rows.map((row) => row.volumetricWeight),
    amounts: rows.map((row) => row.amount.toString()),
    costs: rows.map((row) => (row.totalCents ? (row.totalCents / 100).toFixed(2) : '')),
  };
}

export function buildOptionsSeatPayload(
  seats: PalletSeat[],
  getRowCost: (index: number) => string,
): { options: Record<string, string>[]; amount: number } {
  return seats.reduce(
    (mem, seat, index) => {
      if (seat.amount && seat.option) {
        const amount = +seat.amount;
        mem.amount += amount;
        const costs = distributeRowCost(getRowCost(index), amount);
        mem.options = mem.options.concat(costs.map((cost) => ({ ...seat.option, cost })));
      }
      return mem;
    },
    { options: [] as Record<string, string>[], amount: 0 },
  );
}
